// React
import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import { Button, FieldText } from "components/AtomsV2";
import { BrandLogo, Icon } from "components/ShellV2";

// Assets
import inesctecLogo from "assets/images/inesctec.png";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { selectThemeMode, setThemeMode } from "state/application/applicationSlice";
import { useResetPasswordRequestMutation } from "state/auth/authApiSlice";
import { useGetLandingStatsQuery } from "state/communities/communitiesApiSlice";

function PasswordResetRequest() {
  const { t } = useTranslation(["auth"]);
  const dispatch = useDispatch();
  const themeMode = useSelector(selectThemeMode);
  const isDark = themeMode === "dark";
  const toggleTheme = () => dispatch(setThemeMode(isDark ? "light" : "dark"));

  const [email, setEmail] = useState("");
  const [error, setError] = useState(undefined);
  const [requestResetSent, setRequestResetSent] = useState(false);
  const [buttonText, setButtonText] = useState(t("reset.submit"));

  const [resetPasswordRequest] = useResetPasswordRequestMutation();

  const handleSubmit = async () => {
    if (email === "") {
      return setError(t("reset.errors.email_required"));
    }
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!emailRegex.test(email)) {
      return setError(t("reset.errors.email_invalid"));
    }
    try {
      setButtonText(t("reset.submit_working"));
      await resetPasswordRequest({ email }).unwrap();
      return setRequestResetSent(true);
    } catch {
      setError(t("reset.errors.generic"));
      return setButtonText(t("reset.submit"));
    }
  };

  const { data: stats } = useGetLandingStatsQuery();
  const fmtStat = (n) => (n != null ? Number(n).toLocaleString("pt-PT") : "—");

  const year = new Date().getFullYear();
  const asideStats = [
    [fmtStat(stats?.total_communities), t("login.aside.stat_communities")],
    [fmtStat(stats?.total_members), t("login.aside.stat_members")],
    [fmtStat(stats?.total_resources), t("login.aside.stat_resources")],
  ];

  return (
    <div className="rec-auth">
      <aside className="rec-auth-aside">
        <div className="rec-ring" style={{ width: 520, height: 520, top: -160, right: -160 }} />
        <div className="rec-ring" style={{ width: 340, height: 340, bottom: -120, left: -90 }} />
        <div className="relative flex items-center gap-3">
          <BrandLogo variant="v1" forceWhite height={124} />
        </div>
        <div className="relative">
          <h2 className="text-[34px] font-bold leading-[1.2] tracking-[-0.6px] m-0 mb-[14px]">
            {t("login.aside.headline")}
          </h2>
          <p className="text-[15px] leading-[1.6] text-white/[.78] max-w-[420px] m-0">
            {t("login.aside.copy")}
          </p>
          <div className="flex gap-[30px] mt-8">
            {asideStats.map(([n, l]) => (
              <div key={l}>
                <div className="text-[26px] font-bold tracking-[-0.5px]">{n}</div>
                <div className="text-[12.5px] text-white/[.65] mt-[2px]">{l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-[12.5px] text-white/[.55]">
          {t("login.aside.copyright", { year })}
        </div>
      </aside>

      <div className="rec-auth-form-wrap">
        <div className="rec-auth-card">
          <div className="flex justify-end mb-2">
            <button
              type="button"
              className="rec-icon-btn"
              onClick={toggleTheme}
              title={t("login.theme_toggle")}
            >
              <Icon name={isDark ? "light_mode" : "dark_mode"} fill />
            </button>
          </div>
          <h1 className="text-[26px] font-bold tracking-[-0.4px] m-0 mb-[6px] text-ink">
            {t("reset.title")}
          </h1>
          <p className="text-[14px] text-ink-3 m-0 mb-[26px]">{t("reset.subtitle")}</p>

          {requestResetSent ? (
            <div className="flex flex-col items-center text-center gap-3 py-2">
              <div
                className="flex items-center justify-center"
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 16,
                  background: "var(--accent-soft)",
                  color: "var(--accent)",
                }}
              >
                <Icon name="mark_email_read" fill style={{ fontSize: 28 }} />
              </div>
              <p className="text-[14px] text-ink-2 leading-[1.6] m-0 max-w-[340px]">
                {t("reset.sent")}
              </p>
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
              className="flex flex-col gap-4"
            >
              <FieldText
                id="reset-email"
                label={t("reset.email_label")}
                icon="mail"
                type="email"
                value={email}
                placeholder={t("reset.email_label")}
                onChange={(v) => {
                  setEmail(v);
                  setError(undefined);
                }}
              />
              {error && (
                <div
                  className="text-[13px] font-medium text-center"
                  style={{ color: "var(--danger)" }}
                >
                  {error}
                </div>
              )}
              <Button type="submit" className="h-[46px] text-[15px] mt-1">
                {buttonText}
                <Icon name="arrow_forward" />
              </Button>
            </form>
          )}

          <div className="text-center mt-[22px] text-[13px] text-ink-3">
            <Link
              to="/login"
              className="inline-flex items-center gap-[5px] font-semibold no-underline"
              style={{ color: "var(--accent-deep)" }}
            >
              <Icon name="arrow_back" style={{ fontSize: 15 }} />
              {t("reset.back_to_login")}
            </Link>
          </div>

          <div className="rec-auth-credit">
            <span>{t("login.credit")}</span>
            <img src={inesctecLogo} alt="INESC TEC" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default PasswordResetRequest;
